import React from 'react';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Alert from 'react-bootstrap/Alert';

const EQUIPMENT_TYPES = [
  { id: 'chainsaw', name: 'Chainsaw', category: 'Tree Work' },
  { id: 'wood_chipper', name: 'Wood Chipper', category: 'Tree Work' },
  { id: 'stump_grinder', name: 'Stump Grinder', category: 'Tree Work' },
  { id: 'bucket_truck', name: 'Bucket Truck', category: 'Tree Work' },
  { id: 'crane', name: 'Crane', category: 'Tree Work' },
  { id: 'skid_steer', name: 'Skid Steer', category: 'Debris Hauling' },
  { id: 'dump_truck', name: 'Dump Truck', category: 'Debris Hauling' },
  { id: 'grapple_truck', name: 'Grapple Truck', category: 'Debris Hauling' },
  { id: 'trailer', name: 'Trailer', category: 'Debris Hauling' },
  { id: 'front_loader', name: 'Front Loader', category: 'Debris Hauling' },
  { id: 'excavator', name: 'Excavator', category: 'Heavy Equipment' },
  { id: 'bulldozer', name: 'Bulldozer', category: 'Heavy Equipment' },
];

const EXPERIENCE_LEVELS = [
  { value: '0', label: 'Less than 1 year' },
  { value: '1', label: '1-2 years' },
  { value: '3', label: '3-5 years' },
  { value: '6', label: '6-10 years' },
  { value: '10', label: '10+ years' },
];

function EquipmentExperienceStep({ data, errors, onChange }) {
  const equipmentExperience = data.equipment_experience || {};

  const handleEquipmentToggle = (equipmentId) => {
    const current = equipmentExperience[equipmentId] || { hasExperience: false, years: '0', details: '' };
    onChange({
      equipment_experience: {
        ...equipmentExperience,
        [equipmentId]: { ...current, hasExperience: !current.hasExperience },
      },
    });
  };

  const handleExperienceChange = (equipmentId, field, value) => {
    const current = equipmentExperience[equipmentId] || { hasExperience: true, years: '0', details: '' };
    onChange({
      equipment_experience: {
        ...equipmentExperience,
        [equipmentId]: { ...current, [field]: value },
      },
    });
  };

  const handleGeneralChange = (e) => {
    const { name, value, type, checked } = e.target;
    onChange({ [name]: type === 'checkbox' ? checked : value });
  };

  const groupedEquipment = EQUIPMENT_TYPES.reduce((acc, eq) => {
    if (!acc[eq.category]) acc[eq.category] = [];
    acc[eq.category].push(eq);
    return acc;
  }, {});

  return (
    <div className="equipment-experience-step">
      <h5 className="mb-3">Equipment Experience</h5>
      <p className="text-muted mb-4">
        Select the equipment you have experience operating and indicate your experience level.
      </p>

      {errors.equipment_experience && (
        <Alert variant="danger">{errors.equipment_experience}</Alert>
      )}

      {Object.entries(groupedEquipment).map(([category, equipment]) => (
        <div key={category} className="mb-4">
          <h6 className="text-secondary mb-3">{category}</h6>
          {equipment.map((eq) => {
            const exp = equipmentExperience[eq.id] || { hasExperience: false, years: '0', details: '' };
            return (
              <div key={eq.id} className={`equipment-item ${exp.hasExperience ? 'selected' : ''}`}>
                <div className="equipment-header">
                  <Form.Check
                    type="checkbox"
                    id={`equipment-${eq.id}`}
                    label={eq.name}
                    checked={exp.hasExperience}
                    onChange={() => handleEquipmentToggle(eq.id)}
                  />
                  {exp.hasExperience && (
                    <Form.Select
                      size="sm"
                      value={exp.years}
                      onChange={(e) => handleExperienceChange(eq.id, 'years', e.target.value)}
                      style={{ width: 'auto' }}
                    >
                      {EXPERIENCE_LEVELS.map((level) => (
                        <option key={level.value} value={level.value}>
                          {level.label}
                        </option>
                      ))}
                    </Form.Select>
                  )}
                </div>
                {exp.hasExperience && (
                  <div className="equipment-details">
                    <Form.Control
                      type="text"
                      size="sm"
                      placeholder="Additional details (certifications, specific models, etc.)"
                      value={exp.details}
                      onChange={(e) => handleExperienceChange(eq.id, 'details', e.target.value)}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}

      <hr className="my-4" />

      <h6 className="mb-3">Additional Information</h6>
      <Row className="mb-3">
        <Col xs={12} md={6}>
          <Form.Group controlId="years_in_business">
            <Form.Label>Years in Business</Form.Label>
            <Form.Control
              type="number"
              name="years_in_business"
              min="0"
              value={data.years_in_business}
              onChange={handleGeneralChange}
            />
          </Form.Group>
        </Col>
      </Row>

      <Form.Group className="mb-3" controlId="has_insurance">
        <Form.Check
          type="checkbox"
          name="has_insurance"
          label="I have liability insurance"
          checked={data.has_insurance}
          onChange={handleGeneralChange}
        />
      </Form.Group>

      {data.has_insurance && (
        <Form.Group className="mb-3" controlId="insurance_details">
          <Form.Label>Insurance Details</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            name="insurance_details"
            value={data.insurance_details}
            onChange={handleGeneralChange}
            placeholder="Provider, coverage amount, policy number, etc."
          />
        </Form.Group>
      )}

      <Form.Group className="mb-3" controlId="additional_notes">
        <Form.Label>Additional Notes</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          name="additional_notes"
          value={data.additional_notes}
          onChange={handleGeneralChange}
          placeholder="Any other information you'd like to share..."
        />
      </Form.Group>
    </div>
  );
}

export default EquipmentExperienceStep;
